import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'

import _isEmpty from 'underscore/cjs/isEmpty.js'

import { useUtmsStore } from './utms'

dayjs.extend(customParseFormat)

export const useOvhcStore = defineStore('ovhc', {
  state: () => ({
    guides: {
      url: null,
      initialPage: ''
    },
    
    form: {},
    uuid: undefined,
    lead: {},
    quotesRaw: [],
    quotes: [],
    application: {},
    selectedQuote: {},
    applicationSubmitted: false,
    searchFilters: {},
    applicationForm: {}
  }),

  getters: {
    leadParams () {
      const form = this.form

      if (_isEmpty(form)) {
        return {}
      }

      const utmsStore = useUtmsStore()
      const formData = flattenObj(form, 1)

      const obj = {
        ...getName(formData?.full_name),
        ...(formData.partner_birthdate
          ? { partner_birthdate: dayjs(formData.partner_birthdate, 'DD/MM/YYYY').format('MM/DD/YYYY') }
          : {}),
        life_stage: formData?.life_stage,
        visa_type: `${formData?.visa_type}`.toLowerCase(),
        visa_subtype: formData?.visa_subtype,
        visa_code: formData?.visa_code,
        check_visa_compatibility: formData?.check_visa_compatibility,
        country_of_citizenship: formData?.country_of_citizenship?.name ?? formData?.country_of_citizenship,
        already_in_australia: formData?.already_in_australia,
        expected_entry_date: formData?.expected_entry_date,
        include_pregnancy_related_cover: formData.include_pregnancy_related_cover,
        date_of_birth: dayjs(formData.date_of_birth, 'DD/MM/YYYY').format('MM/DD/YYYY'),
        cover_level: formData?.cover_level,
        email: formData?.email,
        phone: formData?.phone,
        state: formData?.state_in_australia,
        age: formData?.age,
        age_secondary: formData?.age_secondary,
        motivation: formData?.motivation,

        brand: 'CompareClub',
        form_page: import.meta.client && window.location.href,
        landing_page: utmsStore.GET_UTMS_LANDING_PAGE,
        enquiry_source: 'cc_ovhc'
      }

      return obj
    },

    applicationParams () {
      const formData = flattenObj(this.applicationForm)

      const australianAddress = this.applicationForm?.step1?.australian_address

      const leadDetails = this.lead?.deatails
      const uuid = this.lead?.uuid

      const internationalAddress = {
        addressLine1: formData.overseas_address_line1,
        addressLine2: formData.overseas_address_line2,
        city: formData.overseas_city,
        state: formData.overseas_state,
        postcode: formData.overseas_postcode,
        country: this.applicationForm?.step1?.overseas_country?.name
      }

      const selectedQuote = {
        fund: this.selectedQuote.fund_name,
        cover_name: this.selectedQuote.cover_name,
        cover_type: this.selectedQuote.hospital_level,
        premium_month: this.selectedQuote.month_price,
        premium_year: this.selectedQuote.year_price,
        excess: this.selectedQuote.hospital_excess_amount
      }

      const children = formData.children ? JSON.parse(JSON.stringify(formData.children)) : []

      children.forEach(child => {
        if (child.child_birth_date) {
          child.child_birth_date = dayjs(child.child_birth_date, 'DD/MM/YYYY').format('MM/DD/YYYY')
        }
      })

      return {
        uuid,
        dependants: !_isEmpty(children) ? children : [],
        cover_type: leadDetails?.life_stage,
        country_of_citizenship: leadDetails?.country_of_citizenship?.name ?? leadDetails?.country_of_citizenship,
        already_in_australia: leadDetails?.already_in_australia,
        expected_entry_date: dayjs(leadDetails?.expected_entry_date, 'DD/MM/YYYY').format('MM/DD/YYYY'),
        include_pregnancy_related_cover: false,
        date_of_birth: leadDetails?.date_of_birth,
        cover_level: this.selectedQuote.hospital_level,
        first_name: formData.primary_first_name,
        last_name: formData.primary_last_name,
        email: formData.primary_email,
        phone: formData.primary_number,
        title: formData.primary_title,
        gender: formData.primary_gender,
        contact_methods: formData.primary_preferred_contact,
        has_australian_mailing_address: formData.has_australian_mailing_address,
        international_address: internationalAddress,
        australian_address: australianAddress,
        partner_first_name: formData?.partner_first_name,
        partner_last_name: formData?.partner_last_name,
        partner_gender: formData?.partner_gender,
        partner_birthdate: dayjs(formData?.partner_birthdate, 'DD/MM/YYYY').format('MM/DD/YYYY'),
        accept_privacy_policy: 'Yes',
        quote_selected: selectedQuote,
        partner_title: formData?.partner_title,
        has_existing_health_insurance: formData?.existing_health_insurance,
        existing_health_fund: formData?.current_health_fund,
        health_fund_membership_number: formData?.current_membership_number,
        has_pre_existing_health_conditions: formData?.pre_existing_conditions,
        visa_type: formData.visa_subclass,
        estimated_cover_start_date: dayjs(formData?.cover_start_date, 'DD/MM/YYYY').format('MM/DD/YYYY'),
        visa_sponsor_name: formData?.sponsor_name,
        visa_sponsor_email: formData?.sponsor_email,
        visa_sponsor_phone: formData?.sponsor_phone,
        payment_frequency: formData?.payment_frequency,
        payment_method: formData?.payment_type,
        card_expiry: formData?.card_exp_date,
        name_on_card: formData?.cardholder_name,
        user_accept_agreement: true,
        user_accept_declaration: true,
        international_phone: '',
        sessionid: '',
        tokenisation_ref: formData?.card_token,
        cardtype: formData?.payment_type,
        visa_grant_date: formData?.visa_grant_date ? dayjs(formData?.visa_grant_date, 'DD/MM/YYYY').format('MM/DD/YYYY') : '',
        card_cvv: formData?.card_cvv,
        bank_name: formData?.bank_name,
        bank_account_holder_name: formData?.bank_account_holder_name,
        bank_account_number: formData?.bank_account_number,
        bank_bsb: formData?.bank_bsb,
        passport_number: formData?.primary_passport_number
      }
    }
  },

  actions: {
    async createLead (payload) {
      const utmsStore = useUtmsStore()
      const response = await useFetch('/api/lead/create', {
        method: 'post',
        body: {
          vertical: 'OVHC',
          ...utmsStore.utms,
          ...payload
        }
      }).then(res => {
        const temp = res?.data.value

        if (temp?.error) {
          throw temp?.error
        }

        const data = temp?.data

        return data?.lead_uuid ?? undefined
      }).catch(error => {
        throw error
      })

      return response
    }
  }
})
